import { useEffect, useRef } from 'react';
import type { PlatformBridge } from '@slate/core';
import {
  AUTOSAVE_DEBOUNCE_DEFAULT_MS,
  AUTOSAVE_MAX_WAIT_DEFAULT_MS,
  useDocStore,
  type OpenDoc,
} from '../store/docStore';
import { saveDocById } from '../store/docActions';

/**
 * Default idle-debounce + max-wait. Kept as exported constants for tests and
 * back-compat; the live values come from the store so users can tune them
 * (see Save settings panel).
 */
export const AUTOSAVE_DEBOUNCE_MS = AUTOSAVE_DEBOUNCE_DEFAULT_MS;
export const AUTOSAVE_MAX_WAIT_MS = AUTOSAVE_MAX_WAIT_DEFAULT_MS;

/**
 * A doc is auto-saveable only when it has a real saved ref (a file path/handle)
 * AND has unsaved edits. Untitled buffers (null ref) are NEVER silently saved —
 * they need a Save As dialog, so we skip them until first manual save.
 */
function isAutoSaveable(doc: OpenDoc | undefined): doc is OpenDoc {
  return doc != null && doc.ref != null && doc.dirty;
}

/**
 * Debounced autosave engine. When `autosave` is ON, watches the ACTIVE doc and,
 * once it's a dirty file-backed buffer, saves it after ~5s of idle — or sooner
 * via a 30s max-wait if the user keeps editing continuously.
 *
 * The pending save captures the doc id, so a flush still targets the right doc
 * if the active tab changes. On tab switch / unmount / turning autosave off, any
 * pending save is FLUSHED immediately (saved now) so edits aren't lost, and the
 * timer is cleared.
 *
 * The debounce + max-wait are implemented with a single `setTimeout` plus a ref
 * tracking the first-pending timestamp (no new deps). All saves go through
 * `saveDocById`, which is guarded and never throws.
 */
export function useAutosave(platform: PlatformBridge): void {
  const autosave = useDocStore((s) => s.autosave);
  const activeId = useDocStore((s) => s.activeId);
  // User-tunable debounce + max-wait. Defaults match AUTOSAVE_*_DEFAULT_MS.
  const debounceMs = useDocStore((s) => s.autosaveDebounceMs);
  const maxWaitMs = useDocStore((s) => s.autosaveMaxWaitMs);
  // These two primitives make the scheduling effect re-run on every edit to the
  // active doc so the debounce/max-wait clock advances.
  const activeText = useDocStore((s) => s.activeDoc()?.text);
  const activeDirty = useDocStore((s) => s.activeDoc()?.dirty ?? false);

  // The pending timer + the id it's scheduled to save + the timestamp of the
  // first edit since the last save (drives the 30s max-wait). Refs so they
  // survive re-renders and helpers can read/clear them across effects.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIdRef = useRef<string | null>(null);
  const firstPendingRef = useRef<number>(0);

  // Latest platform in a ref so flush (called from cleanups that don't depend on
  // `platform`) always uses the current bridge.
  const platformRef = useRef(platform);
  platformRef.current = platform;

  const clearTimer = (): void => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Save whatever is currently pending (if still saveable), now, and reset the
  // pending state. Used by the tab-switch / autosave-off / unmount paths.
  const flushRef = useRef<() => void>(() => {});
  flushRef.current = (): void => {
    clearTimer();
    const id = pendingIdRef.current;
    pendingIdRef.current = null;
    firstPendingRef.current = 0;
    if (id == null) return;
    const doc = useDocStore.getState().docs.find((d) => d.id === id);
    if (!isAutoSaveable(doc)) return; // clean/unknown/untitled → nothing to do
    void saveDocById(platformRef.current, id);
  };

  // --- Flush-on-transition -------------------------------------------------
  // Flush a pending save when the ACTIVE doc changes (the prior doc's edits must
  // land even though it's no longer active) and on unmount.
  const prevActiveIdRef = useRef<string | null>(activeId);
  useEffect(() => {
    if (prevActiveIdRef.current !== activeId) {
      flushRef.current();
      prevActiveIdRef.current = activeId;
    }
  }, [activeId]);

  // Flush when autosave is turned OFF (so a save in flight isn't dropped).
  const prevAutosaveRef = useRef<boolean>(autosave);
  useEffect(() => {
    if (prevAutosaveRef.current && !autosave) flushRef.current();
    prevAutosaveRef.current = autosave;
  }, [autosave]);

  // Flush when the view mode changes (plaintext ↔ wysiwyg ↔ rendered). The
  // outgoing editor unmounts and we don't want its pending edits to ride along
  // into the next view's first-keystroke debounce window.
  const viewMode = useDocStore((s) => s.viewMode);
  const prevViewModeRef = useRef(viewMode);
  useEffect(() => {
    if (prevViewModeRef.current !== viewMode) {
      flushRef.current();
      prevViewModeRef.current = viewMode;
    }
  }, [viewMode]);

  // Flush on window blur (user switched apps / minimised / clicked outside the
  // window) and on the page going hidden (browser-tab background). Both are
  // "user left the editor" signals — we want their edits on disk even if the
  // app is closed while away.
  useEffect(() => {
    if (!autosave) return;
    if (typeof window === 'undefined') return;
    const onBlur = (): void => flushRef.current();
    const onVisibility = (): void => {
      if (document.hidden) flushRef.current();
    };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [autosave]);

  // Flush when focus leaves an editor pane — e.g. clicking the toolbar, the
  // sidebar, or the rendered preview. We detect this by listening for
  // `focusout` at the document and checking that the previously-focused element
  // was inside an `.mp-editor` container while the next isn't (or is null,
  // i.e. the window is losing focus entirely — which the window-blur listener
  // also catches; the duplicate flush is harmless because flush is idempotent
  // when there's nothing pending).
  useEffect(() => {
    if (!autosave) return;
    if (typeof document === 'undefined') return;
    const onFocusOut = (e: FocusEvent): void => {
      const src = e.target as HTMLElement | null;
      if (!src?.closest?.('.mp-editor')) return;
      const next = e.relatedTarget as HTMLElement | null;
      if (next && next.closest?.('.mp-editor')) return; // moved within editor
      flushRef.current();
    };
    document.addEventListener('focusout', onFocusOut);
    return () => document.removeEventListener('focusout', onFocusOut);
  }, [autosave]);

  // Flush on unmount.
  useEffect(() => () => flushRef.current(), []);

  // --- Scheduling ----------------------------------------------------------
  // Re-runs on every edit (activeText/activeDirty) to (re)arm the debounce, but
  // does NOT flush on its own cleanup — flushing is handled by the transition
  // effects above so a normal keystroke re-render never forces an early save.
  useEffect(() => {
    if (!autosave || activeId == null) {
      clearTimer();
      return;
    }
    const doc = useDocStore.getState().docs.find((d) => d.id === activeId);
    if (!isAutoSaveable(doc)) {
      // Nothing to save for the active doc (clean / untitled). Clear any pending
      // schedule that targeted THIS doc (it just got saved/cleaned).
      if (pendingIdRef.current === activeId) {
        clearTimer();
        pendingIdRef.current = null;
        firstPendingRef.current = 0;
      }
      return;
    }

    const now = Date.now();
    // Start (or keep) the max-wait clock from the first pending edit since the
    // last save for this doc.
    if (pendingIdRef.current !== activeId || firstPendingRef.current === 0) {
      pendingIdRef.current = activeId;
      firstPendingRef.current = now;
    }

    clearTimer();
    const elapsed = now - firstPendingRef.current;
    // Force a save by the max-wait deadline; otherwise debounce on idle (but
    // never wait past the deadline).
    const delay =
      elapsed >= maxWaitMs
        ? 0
        : Math.min(debounceMs, maxWaitMs - elapsed);

    const idToSave = activeId;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (pendingIdRef.current !== idToSave) return; // superseded by a switch
      pendingIdRef.current = null;
      firstPendingRef.current = 0;
      const live = useDocStore.getState().docs.find((d) => d.id === idToSave);
      if (!isAutoSaveable(live)) return;
      void saveDocById(platformRef.current, idToSave);
    }, delay);

    // No flush in cleanup — see comment above. Just stop the timer; the next run
    // reschedules with the advanced max-wait clock.
    return clearTimer;
  }, [autosave, activeId, activeText, activeDirty, debounceMs, maxWaitMs]);
}
